import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  CircularProgress,
  Typography, ListItemButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import React, { FC, useState } from 'react';
import { AutoTemplate } from 'types/auto-templates';

import { paneMaxHeight } from '../utils/pane-height';
import { ConfirmDelete } from './modal-confirm-delete';

const useStyles = makeStyles((theme: any) => ({
  paper: {
    padding: theme.spacing(2),
    ...paneMaxHeight(theme),
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  content: {
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    flex: 1,
  },
  touchTarget: {
    minHeight: theme.spacing(5.5),
    minWidth: theme.spacing(5.5),
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  row: {
    minHeight: theme.spacing(5.5),
    minWidth: 0,
  },
}));

type ListTemplatesProps = {
  templates: AutoTemplate[];
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleDeleteTemplate: (id: string) => Promise<boolean>;
  isDeleting: boolean;
  setSelectedTemplate: React.Dispatch<
    React.SetStateAction<AutoTemplate | undefined>
  >;
  selectedTemplate: AutoTemplate | undefined;
  isLoading: boolean;
  isError: boolean;
  t: TFunction;
};

export const ListTemplates: FC<ListTemplatesProps> = ({
  templates,
  setOpen,
  handleDeleteTemplate,
  isDeleting,
  setSelectedTemplate,
  selectedTemplate,
  isLoading,
  isError,
  t,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedTemplateID, setSelectedTemplateID] = useState('');
  const classes = useStyles();

  return (
    <Paper className={classes.paper}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{t('documents:list-templates')}</Typography>
        <Button
          variant="outlined"
          color="secondary"
          aria-label={t('documents:new-template')}
          className={classes.touchTarget}
          onClick={() => setOpen(true)}
        >
          <FontAwesomeIcon icon="plus" />
        </Button>
      </Box>

      <Box className={classes.content}>
        {isLoading && (
          <Box display="flex" justifyContent="center" p={2} role="status">
            <CircularProgress size={24} />
          </Box>
        )}
        {isError && !isLoading && (
          <Box p={2} role="status">
            <Typography color="error">{t('documents:templates-error')}</Typography>
          </Box>
        )}
        {!isLoading && !isError && templates.length === 0 && (
          <Box p={2}>
            <Typography color="textSecondary">{t('documents:templates-empty')}</Typography>
          </Box>
        )}
        {!isLoading && !isError && templates.length > 0 && (
          <List>
            {templates.map((template) => (
              <ListItem
                disablePadding
                key={template._id}
                className={classes.row}
              >
                <ListItemButton
                  selected={selectedTemplate?._id === template._id}
                  onClick={() => setSelectedTemplate(template)}
                >
                  <ListItemText primary={template.name} primaryTypographyProps={{ noWrap: true }} />
                </ListItemButton>
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label={t('documents:delete-template')}
                    className={classes.touchTarget}
                    disabled={isDeleting}
                    onClick={() => {
                      setSelectedTemplateID(template._id);
                      setOpenModal(true);
                    }}
                    size="large"
                  >
                    <FontAwesomeIcon icon="trash" size="xs" className={classes.deleteIcon} />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      <ConfirmDelete
        openModal={openModal}
        isDeleting={isDeleting}
        handleDeleteTemplate={() => handleDeleteTemplate(selectedTemplateID)}
        closeModal={() => setOpenModal(false)}
      />
    </Paper>
  );
};
